import {HexMath} from '../HexMath';
import {ConstructionAccessibility} from "../ConstructionAccessibility";
import {
  COMMON_BUILDING_TOKENS,
  TOKEN_NAME_MAPPING,
  TOKENS_RELIED_ON_QUARTER,
  TOKENS_RESTRICTED_FOR_BUILDING_NEAR_ENEMY_QUARTERS
} from "../../constants/tokens";
import {checkIfCentralQuarter} from "../buildings/checkIfCentralQuarter";
import {checkIfAnyQuarter} from "../buildings/checkIfAnyQuarter";
import {checkIfOwner} from "./checkIfOwner";
import {CONSTRUCTION_ACTION_TYPES, QUARTERS_OF_DIFFERENT_CITIES_MIN_HEXES_BETWEEN} from "../../constants/construction";
import {checkIfBaseBuildingDamaged} from "./checkIfBaseBuildingDamaged";

/**
 * Утилита для валидации отложенных строений при завершении фазы строительства
 */

const hexMath = new HexMath(30, 'flat');

/**
 * Получить все кварталы города (BFS)
 */
const getCityQuarters = (startQ, startR, hexesCopy) => {
  const visited = new Set();
  const cityQuarters = [];
  const queue = [[startQ, startR]];

  while (queue.length > 0) {
    const [cq, cr] = queue.shift();
    const key = `${cq},${cr}`;

    if (visited.has(key)) continue;
    visited.add(key);

    const tokenType = hexesCopy[key];
    if (checkIfAnyQuarter(tokenType)) {
      cityQuarters.push({q: cq, r: cr, key});

      // Добавляем соседей для продолжения поиска
      const nextNeighbors = hexMath.getNeighborsFlatTop(cq, cr);
      for (const [nnq, nnr] of nextNeighbors) {
        const nKey = `${nnq},${nnr}`;
        const nToken = hexesCopy[nKey];
        if (checkIfAnyQuarter(nToken) && !visited.has(nKey)) {
          queue.push([nnq, nnr]);
        }
      }
    }
  }
  return cityQuarters;
};

/**
 * Получить все кварталы из hexes
 */
const getAllQuartersFromHexes = (hexesCopy) => {
  const quarters = [];
  for (const [key, tokenType] of Object.entries(hexesCopy)) {
    const [q, r] = key.split(',').map(Number);
    if (checkIfAnyQuarter(tokenType)) {
      quarters.push({q, r, tokenType, key});
    }
  }
  return quarters;
};

/**
 * Валидировать отложенные строения игрока
 * @param {Array} pendingBuildings - Массив отложенных строений
 * @param {Object} hexes - Текущее состояние гексов
 * @param currentPlayer
 * @param players
 * @param citiesCache
 * @param buildingsDamage
 * @param specialBuildings - спецстроения по ячейкам: пока спецстроение цело,
 *   весь урон ячейки принадлежит ему, а не базовому строению под ним
 * @returns {Object} Результат валидации: { isValid, invalidBuildings, validBuildings }
 */
export const validatePendingBuildings = (pendingBuildings, hexes, currentPlayer, players, citiesCache, buildingsDamage, specialBuildings) => {
  let newHexes = {...hexes};
  const validBuildings = [];
  const invalidBuildings = [];

  // Проверяем каждое строение
  for (const building of pendingBuildings) {
    if (building.type === CONSTRUCTION_ACTION_TYPES.BUILDING_REPAIRMENT) {
      continue;
    }

    const [q, r] = building.hexKey.split(',').map(Number);
    const tokenType = building.token;
    const tokenName = TOKEN_NAME_MAPPING[tokenType];

    const validationErrors = [];

    const isRestrictedForBuildingNearEnemyQuarter = TOKENS_RESTRICTED_FOR_BUILDING_NEAR_ENEMY_QUARTERS.includes(tokenType);
    if (isRestrictedForBuildingNearEnemyQuarter) {
      const isAdjacentToEnemyQuarter = checkIfAdjacentToEnemyQuarter(building.hexKey, hexes, currentPlayer, players, citiesCache, pendingBuildings)
      if (isAdjacentToEnemyQuarter) {
        validationErrors.push(`${tokenName} - нельзя строить смежно кварталам противника`);
      }
    }

    if (checkIfCentralQuarter(tokenType)) {
      // Центральный квартал: нельзя размещать, если на расстоянии до 2 ячеек (с учетом гор) есть любой квартал другого города
      const tempHexes = {...hexes};
      delete tempHexes[building.hexKey];
      const allQuarters = getAllQuartersFromHexes(tempHexes);
      
      const constructionAccessibility = new ConstructionAccessibility(hexMath);
      
      // Получаем все ячейки достижимые из строящегося квартала за QUARTERS_OF_DIFFERENT_CITIES_MIN_HEXES_BETWEEN ходов
      const reachableFromNewQuarter = constructionAccessibility.findReachableCells(
        building.hexKey,
        QUARTERS_OF_DIFFERENT_CITIES_MIN_HEXES_BETWEEN,
        tempHexes
      );
      
      // Проверяем есть ли в достижимых ячейках кварталы других городов
      for (const quarter of allQuarters) {
        // Пропускаем смежные базовые кварталы
        const distance = hexMath.hexDistance(q, r, quarter.q, quarter.r);
        if (distance === 1 && quarter.tokenType === COMMON_BUILDING_TOKENS.QUARTER) {
          continue;
        }
        if (reachableFromNewQuarter.has(quarter.key)) {
          validationErrors.push('Центральный квартал - нельзя строить ближе 2 ячеек от кварталов другого города');
          break;
        }
      }
    } else if (tokenType === COMMON_BUILDING_TOKENS.QUARTER) {
      // Базовый квартал: должен быть смежен центральному кварталу игрока
      const neighbors = hexMath.getNeighborsFlatTop(q, r);
      let hasAdjacentCentralQuarter = false;
      let playerCentralQuarter = null;

      for (const [nq, nr] of neighbors) {
        const neighborKey = `${nq},${nr}`;
        const neighborToken = newHexes[neighborKey];
        if (checkIfCentralQuarter(neighborToken)) {
          hasAdjacentCentralQuarter = true;
          playerCentralQuarter = {q: nq, r: nr, key: neighborKey, token: neighborToken};
          break;
        }
      }

      if (!hasAdjacentCentralQuarter) {
        validationErrors.push('Базовый квартал - смежно должен быть Центральный квартал');
      }

      // Повреждение считается по БАЗОВОМУ строению ячейки: пока на центральном
      // квартале цела крепость, весь урон ячейки принадлежит ей, а сам квартал
      // не повреждён — раньше прямая проверка buildingsDamage > 0 запрещала
      // стройку из-за подбитой катапультой крепости
      if (playerCentralQuarter
        && checkIfBaseBuildingDamaged(playerCentralQuarter.key, buildingsDamage, specialBuildings)) {
        validationErrors.push('Базовый квартал - нельзя строить смежно поврежденному Центральному кварталу. Сначала отремонтируйте Центральный квартал.');
      }

      // Проверяем расстояние до других городов (исключая свой город)
      const tempHexes = {...hexes};
      delete tempHexes[building.hexKey];
      const allQuarters = getAllQuartersFromHexes(tempHexes);

      // Получаем кварталы своего города
      const ownCityQuarters = playerCentralQuarter ?
        getCityQuarters(playerCentralQuarter.q, playerCentralQuarter.r, newHexes) : [];
      const ownCityKeys = new Set(ownCityQuarters.map(c => c.key));
      
      const constructionAccessibility = new ConstructionAccessibility(hexMath);
      
      // Получаем все ячейки достижимые из строящегося квартала за QUARTERS_OF_DIFFERENT_CITIES_MIN_HEXES_BETWEEN ходов
      const reachableFromNewQuarter = constructionAccessibility.findReachableCells(
        building.hexKey,
        QUARTERS_OF_DIFFERENT_CITIES_MIN_HEXES_BETWEEN,
        tempHexes
      );

      // Проверяем расстояние до чужих городов (исключая свой город)
      for (const quarter of allQuarters) {
        if (ownCityKeys.has(quarter.key)) {
          continue; // Пропускаем свой город
        }
        if (reachableFromNewQuarter.has(quarter.key)) {
          validationErrors.push('Базовый квартал - нельзя строить ближе 2 ячеек от кварталов другого города');
          break;
        }
      }
    } else if (TOKENS_RELIED_ON_QUARTER.includes(tokenType)) {
      // Ферма или казарма: должна быть смежна кварталу игрока (базовому или центральному)
      const neighbors = hexMath.getNeighborsFlatTop(q, r);
      let hasAdjacentQuarter = false;

      for (const [nq, nr] of neighbors) {
        const neighborKey = `${nq},${nr}`;
        const neighborToken = newHexes[neighborKey];
        if (checkIfCentralQuarter(neighborToken)
          || neighborToken === COMMON_BUILDING_TOKENS.QUARTER
        ) {
          hasAdjacentQuarter = true;
          break;
        }
      }

      if (!hasAdjacentQuarter) {
        validationErrors.push(`${tokenName} -  смежно должен быть Центральный или Базовый квартал`);
      }
    }

    if (validationErrors.length === 0) {
      validBuildings.push(building);
    } else {
      invalidBuildings.push({
        ...building,
        validationErrors,
      });
    }
  }

  return {
    isValid: invalidBuildings.length === 0,
    invalidBuildings,
    validBuildings
  };
};

const checkIfAdjacentToEnemyQuarter = (hexKey, hexes, currentPlayer, players, citiesCache, pendingBuildings) => {
  const [q, r] = hexKey.split(',').map(Number);
  const hexMath = new HexMath(30, 'flat');
  const neighbors = hexMath.getNeighborsFlatTop(q, r);


  for (const neighbor of neighbors) {
    const [q, r] = neighbor;
    const neighborKey = `${q},${r}`;
    // Проверяем, есть ли на соседнем гексе military_camp
    const token = hexes[neighborKey];

    if (checkIfAnyQuarter(token)) {
      const pendingQuarter = pendingBuildings.find(i => i.hexKey === neighborKey);
      if (pendingQuarter) {
        continue;
      }

      const isCurrentPlayerOwner = checkIfOwner(neighborKey, currentPlayer.color, citiesCache)

      if (!isCurrentPlayerOwner) {
        return true;
      }
    }
  }

  return false;
}
